import { useRef, useEffect, ReactNode } from "react";

import ChartDataLabels from "chartjs-plugin-datalabels";

import {
  Chart as ChartJS,
  RadialLinearScale,
  PointElement,
  LineElement,
  Filler,
  Tooltip,
  Legend,
} from "chart.js";

ChartJS.register(RadialLinearScale, PointElement, LineElement, Filler, Tooltip, Legend, ChartDataLabels);
import type { ChartJSOrUndefined } from "react-chartjs-2/dist/types"; 

// react-chartjs-2 components
import { Radar } from "react-chartjs-2";

// @mui material components
import Card from "@mui/material/Card";
import Icon from "@mui/material/Icon";

// Material Dashboard 2 PRO React TS components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";

// RadarChart configurations
import configs from "parts/Charts/RadarChart/configs";

// Material Dashboard 2 PRO React TS Base Styles
import colors from "assets/theme/base/colors";

// Material Dashboard 2 PRO React TS Helper Functions
import rgba from "assets/theme/functions/rgba";

// Declaring props types for RadarChart
interface Props {
  icon?: {
    color?: "primary" | "secondary" | "info" | "success" | "warning" | "error" | "light" | "dark";
    component: ReactNode;
  };
  title?: string;
  description?: string | ReactNode;
  chart: {
    labels: string[];
    datasets: {
      label: string;
      color: "primary" | "secondary" | "info" | "success" | "warning" | "error" | "light" | "dark";
      data: number[];
      borderDash?: number[];
    }[];
  };
  [key: string]: any;
}

function RadarChart({ icon, title, description, chart }: Props): JSX.Element {
  const chartRef = useRef<ChartJSOrUndefined<"radar">>(null);

  const chartDatasets = chart.datasets.map((dataset) => ({
    ...dataset,
    backgroundColor: colors[dataset.color]
      ? rgba(colors[dataset.color || "dark"].main, 0.2)
      : rgba(colors.dark.main, 0.2),
  }));

  const { data, options } = configs(chart.labels, chartDatasets);

  useEffect(() => {
    // 初回マウント or 更新時にサイズ強制再調整
    if (chartRef.current) {
      chartRef.current.resize();
    }
  }, [chart]);

  const renderChart = (
    <MDBox py={2} pr={2} pl={icon.component ? 1 : 2}>
      {title || description ? (
        <MDBox display="flex" px={description ? 1 : 0} pt={description ? 1 : 0}>
          {icon.component && (
            <MDBox
              width="4rem"
              height="4rem"
              bgColor={icon.color || "info"}
              variant="gradient"
              coloredShadow={icon.color || "info"}
              borderRadius="xl"
              display="flex"
              justifyContent="center"
              alignItems="center"
              color="white"
              mt={-5}
              mr={2}
            >
              <Icon fontSize="medium">{icon.component}</Icon>
            </MDBox>
          )}
          <MDBox mt={icon.component ? -2 : 0}>
            {title && <MDTypography variant="h6">{title}</MDTypography>}
            <MDBox mb={2}>
              <MDTypography component="div" variant="button" color="text">
                {description}
              </MDTypography>
            </MDBox>
          </MDBox>
        </MDBox>
      ) : null}
      <MDBox p={6} sx={{ width: "100%", maxWidth: 400 }}>
        <Radar ref={chartRef} data={data} options={options} />
      </MDBox>
    </MDBox>
  );

  return title || description ? <Card sx={{ backgroundColor: '#e3f2fd' }}>{renderChart}</Card> : renderChart;
}

// Declaring default props for RadarChart
RadarChart.defaultProps = {
  icon: { color: "info", component: "" },
  title: "",
  description: "",
};

export default RadarChart;
